import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Grid, IconButton, InputAdornment, InputLabel,
    Select, FormControl, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { Delete as IconDelete, Search as IconSearch } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import diagnosticoService from "../../services/diagnosticoService";
import * as catalogoService from "../../services/catalogoService";
import comprobanteService from "../../services/comprobanteService";
import MantenimientoForm from "../../components/common/MantenimientoForm";
import DialogoBusqueda from "../../components/common/DialogoBusqueda";
import PersonSearch from "../../components/PersonSearch";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento de un diagnostico en una sola pagina.
//
// El diagnostico se registra sobre la PERSONA: no se elige una atencion. Al
// guardar uno nuevo la API crea la atencion de esa persona y su detalle con el
// tipo de servicio Diagnosticos, asi que el diagnostico queda con su numero
// dentro de esa atencion recien creada.
//
// Cuando ya existe, se muestra en solo lectura la atencion a la que pertenece,
// leida desde su detalle. Y se puede registrar o buscar el comprobante con el
// que se factura.

const ENTIDAD_NUEVA = {
    idAtencion: 0,
    idPersona: 0,
    persona: "",
    numero: 0,
    idPrograma: 0,
    programa: "",
    fecha: "",
    idPuntuacion: 0,
    puntuacion: "",
    idComprobante: 0,
    comprobante: "",
    observacion: "",
};

const Edit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { isReadOnly } = useMantenimiento("diagnostico");

    const idAtencion = parseInt(params.idAtencion || "0");
    const numero = parseInt(params.numero || "0");
    const nuevo = numero == 0;

    const [entity, setEntity] = useState({ ...ENTIDAD_NUEVA, fecha: moment().format('yyyy-MM-DD') });

    // Referencia a la atencion, leida desde el detalle del diagnostico.
    const [referencia, setReferencia] = useState(null);

    const [listPrograma, setListPrograma] = useState([]);
    const [listPuntuacion, setListPuntuacion] = useState([]);

    const [guardando, setGuardando] = useState(false);

    // Lupa de la persona.
    const [buscandoPersona, setBuscandoPersona] = useState(false);
    const [personas, setPersonas] = useState([]);
    const [filtroPersonas, setFiltroPersonas] = useState({ numeroDocumento: "", nombres: "" });

    // Comprobante del diagnostico: se busca por item.
    const [buscandoComprobante, setBuscandoComprobante] = useState(false);
    const [items, setItems] = useState([]);
    const [filtrosComprobante, setFiltrosComprobante] = useState({ serie: "", concepto: "", idEstadoPago: 0 });

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const avisar = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    }

    const fetchEntity = useCallback(async () => {
        if (idAtencion <= 0 || numero <= 0) {
            setEntity({ ...ENTIDAD_NUEVA, fecha: moment().format('yyyy-MM-DD') });
            setReferencia(null);
            return;
        }

        const diagnosticos = await diagnosticoService.getsDiagnostico().catch(() => []);
        const encontrado = (diagnosticos || []).find((x) => x.idAtencion == idAtencion && x.numero == numero);

        if (!encontrado) {
            avisar("error", "No se encontro el diagnostico.");
            return;
        }

        setEntity({
            idAtencion: encontrado.idAtencion,
            idPersona: encontrado.idPersona || 0,
            persona: encontrado.persona || "",
            numero: encontrado.numero,
            idPrograma: encontrado.idPrograma,
            programa: encontrado.programa || "",
            fecha: encontrado.fecha ? moment(encontrado.fecha).format('yyyy-MM-DD') : "",
            idPuntuacion: encontrado.idPuntuacion || 0,
            puntuacion: encontrado.puntuacion || "",
            idComprobante: encontrado.idComprobante || 0,
            comprobante: encontrado.comprobante || "",
            observacion: encontrado.observacion || "",
        });

        // La referencia a la atencion sale del detalle del diagnostico.
        const det = await diagnosticoService.getsAtencionPorDiagnostico(idAtencion, numero).catch(() => null);

        setReferencia(det);
    }, [idAtencion, numero])

    const fetchListPrograma = useCallback(async () => {
        const data = await diagnosticoService.getsPrograma().catch(() => []);
        setListPrograma(data || []);
    }, [])

    const fetchListPuntuacion = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1).catch(() => []);
        setListPuntuacion(data || []);
    }, [])

    // El diagnostico se recarga cada vez que cambia el identificador de la ruta:
    // al guardar uno nuevo se navega a /diagnostico/edit/:idAtencion/:numero y
    // el componente es el mismo, asi que tiene que volver a leer los datos para
    // que aparezcan la atencion y su detalle.
    useEffect(() => {
        fetchEntity();
    }, [idAtencion, numero])

    useEffect(() => {
        fetchListPrograma();
        fetchListPuntuacion();
    }, [])

    const postEntity = async () => {
        // Solo se elige la persona: la atencion la crea el guardado.
        if (entity.idPersona <= 0) {
            avisar("error", "Indique la persona del diagnostico.");
            return;
        }

        if (entity.idPrograma <= 0) {
            avisar("error", "Indique el programa del diagnostico.");
            return;
        }

        setGuardando(true);

        const resultado = await diagnosticoService.addUpdateDiagnostico({
            ...entity,
            idPuntuacion: entity.idPuntuacion > 0 ? entity.idPuntuacion : null,
            idComprobante: entity.idComprobante > 0 ? entity.idComprobante : null,
        }).catch((err) => { console.log("Err: ", err); return null; });

        setGuardando(false);

        if (!resultado) {
            avisar("error", "No se pudo guardar el diagnostico.");
            return;
        }

        avisar("success", "Se guardaron los datos del diagnostico con exito.");

        // Se navega siempre a la ruta del diagnostico guardado: al crear uno nuevo
        // cambia de 0/0 a la atencion que se creo, y el efecto de arriba la
        // recarga para mostrar su referencia.
        navigate("/diagnostico/edit/" + resultado.idAtencion + "/" + resultado.numero, { replace: true });
    }

    // Lupa de la persona del diagnostico.
    const buscarPersonas = async () => {
        const datos = await import("../../services/personaService");

        const lista = await datos
            .getPersonaResumenBasicoViewByNumeroDocumentoYNombres(filtroPersonas.numeroDocumento, filtroPersonas.nombres)
            .catch((err) => { console.log("Err: ", err); return []; });

        setPersonas(lista || []);
    }

    const elegirPersona = (persona) => {
        // La vista de personas trae dos identificadores: Id (interno de la vista) e
        // IdPersona, que es el que espera la atencion que se crea al guardar.
        const id = persona.idPersona ?? persona.id;
        setEntity((prev) => ({ ...prev, idPersona: id, persona: persona.nombres }));
        setBuscandoPersona(false);
    }

    const buscarItems = async () => {
        const lista = await comprobanteService.getsComprobanteDetalleBusqueda({
            serie: filtrosComprobante.serie,
            numero: "",
            concepto: filtrosComprobante.concepto,
            idEstadoPago: filtrosComprobante.idEstadoPago,
            idEstado: 0,
        }).catch((err) => { console.log("Err: ", err); return []; });

        setItems(lista || []);
    }

    const elegirComprobante = (item) => {
        setEntity((prev) => ({
            ...prev,
            idComprobante: item.idComprobante,
            comprobante: item.serieNumero + " - Item " + String(item.numero).padStart(3, "0"),
        }));
        setBuscandoComprobante(false);
    }

    const quitarComprobante = () => {
        setEntity((prev) => ({ ...prev, idComprobante: 0, comprobante: "" }));
    }

    const columnasItems = [
        { field: 'serieNumero', headerName: 'Serie-Numero', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fecha', headerName: 'Fecha', width: 110, type: 'date', sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY') },
        { field: 'cantidad', headerName: 'Cantidad', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'conceptoCobro', headerName: 'Concepto de Cobro', width: 260, sortable: false, headerAlign: 'left' },
        { field: 'subTotal', headerName: 'Sub Total', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Diagnostico"}
                backTo="/diagnostico"
                onSave={postEntity}
                disabled={isReadOnly}
            >
                <Grid container spacing={1} sx={{
                    "& .MuiGrid-root>.MuiGrid-item": {
                        paddingTop: "8px"
                    },
                }}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                    <Typography variant="h7">Datos del Diagnostico</Typography>
                                </Box>
                                <Grid container spacing={1} alignItems={"center"}>
                                    {/* La persona se elige con la lupa y nunca se
                                        escribe a mano. */}
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            required
                                            name="persona"
                                            label="Persona"
                                            fullWidth
                                            value={entity.persona}
                                            size="small"
                                            disabled={isReadOnly || guardando || !nuevo}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={"Buscar persona"} arrow>
                                                            <IconButton aria-label={"buscar persona"} size="small" edge="end"
                                                                disabled={isReadOnly || guardando || !nuevo}
                                                                onClick={() => setBuscandoPersona(true)}>
                                                                <IconSearch fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            required
                                            name="fecha"
                                            label="Fecha"
                                            type="date"
                                            fullWidth
                                            value={entity.fecha}
                                            onChange={(event) => setEntity({ ...entity, fecha: event.target.value })}
                                            disabled={isReadOnly || guardando}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    {/* Referencia a la atencion, leida desde el detalle. */}
                                    <Grid item xs={6} sm={5}>
                                        <TextField
                                            name="atencion"
                                            label="Atencion"
                                            fullWidth
                                            value={referencia ? String(referencia.idAtencion).padStart(4, "0") : ""}
                                            disabled
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField
                                            name="referenciaDetalle"
                                            label="Referencia del Detalle"
                                            fullWidth
                                            value={referencia ? (referencia.tipoServicio + " - " + referencia.referencia) : ""}
                                            disabled
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={6} sm={6}>
                                        <FormControl required fullWidth>
                                            <InputLabel htmlFor="input-programa">Programa</InputLabel>
                                            <Select
                                                native
                                                label="Programa"
                                                value={entity.idPrograma}
                                                onChange={(event) => {
                                                    const id = parseInt(event.target.value);
                                                    const elegido = listPrograma.find((x) => x.idPrograma == id);

                                                    setEntity((prev) => ({
                                                        ...prev,
                                                        idPrograma: id,
                                                        programa: elegido ? elegido.descripcion : "",
                                                    }));
                                                }}
                                                inputProps={{ name: 'idPrograma', id: 'input-programa' }}
                                                disabled={isReadOnly || guardando}
                                                size="small"
                                            >
                                                <option key={0} value={0}>{"(Seleccione)"}</option>
                                                {listPrograma.map((row) => (
                                                    <option key={row.idPrograma} value={row.idPrograma}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel htmlFor="input-puntuacion">Puntuacion</InputLabel>
                                            <Select
                                                native
                                                label="Puntuacion"
                                                value={entity.idPuntuacion}
                                                onChange={(event) => setEntity({ ...entity, idPuntuacion: parseInt(event.target.value) })}
                                                inputProps={{ name: 'idPuntuacion', id: 'input-puntuacion' }}
                                                disabled={isReadOnly || guardando}
                                                size="small"
                                            >
                                                <option key={0} value={0}>{"(Seleccione)"}</option>
                                                {listPuntuacion.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            name="observacion"
                                            label="Observacion"
                                            fullWidth
                                            multiline
                                            value={entity.observacion}
                                            onChange={(event) => setEntity({ ...entity, observacion: event.target.value })}
                                            disabled={isReadOnly || guardando}
                                            size="small"
                                            InputLabelProps={{ shrink: true, sx: { overflow: "visible", lineHeight: "1.6" } }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Comprobante con el que se factura el diagnostico. */}
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                    <Typography variant="h7">Comprobante</Typography>
                                </Box>
                                <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px" }}>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            name="comprobante"
                                            label="Comprobante"
                                            fullWidth
                                            value={entity.comprobante || ""}
                                            size="small"
                                            disabled={isReadOnly || guardando}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={"Buscar comprobante"} arrow>
                                                            <IconButton aria-label={"buscar comprobante"} size="small" edge="end"
                                                                disabled={isReadOnly || guardando}
                                                                onClick={() => setBuscandoComprobante(true)}>
                                                                <IconSearch fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1} display={"flex"} alignItems={"center"}>
                                        <Tooltip title={"Quitar comprobante"}>
                                            <span>
                                                <IconButton aria-label={"quitar comprobante"} size="medium"
                                                    disabled={isReadOnly || guardando || !entity.idComprobante}
                                                    onClick={quitarComprobante}>
                                                    <IconDelete fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Lupa de la persona del diagnostico (ventana estandar). */}
            <PersonSearch
                open={buscandoPersona}
                openClose={() => setBuscandoPersona(false)}
                setSelection={elegirPersona}
            />

            {/* Lupa del comprobante: se elige el item con el que se factura. */}
            <DialogoBusqueda
                open={buscandoComprobante}
                onClose={() => setBuscandoComprobante(false)}
                titulo="Buscar Detalle del Comprobante"
                columns={columnasItems}
                rows={items}
                rowKey={(row) => row.idComprobante + "-" + row.numero}
                onAceptar={elegirComprobante}
                filtros={
                    <Grid container spacing={1} alignItems={"center"}>
                        <Grid item xs={4} sm={2}>
                            <TextField name="serie" label="Serie-Numero" value={filtrosComprobante.serie} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, serie: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <TextField name="concepto" label="Concepto de Cobro" value={filtrosComprobante.concepto} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, concepto: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-estadopagodiag">Estado Pago</InputLabel>
                                <Select
                                    native
                                    label="Estado Pago"
                                    value={filtrosComprobante.idEstadoPago}
                                    onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, idEstadoPago: parseInt(event.target.value) })}
                                    inputProps={{ name: 'idEstadoPago', id: 'input-estadopagodiag' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    <option key={144} value={144}>{"Pendiente"}</option>
                                    <option key={145} value={145}>{"Cancelado"}</option>
                                    <option key={227} value={227}>{"Pago Consolidado"}</option>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Button variant={"outlined"} size={"medium"} fullWidth onClick={() => { buscarItems(); }}>
                                <IconSearch />
                            </Button>
                        </Grid>
                    </Grid>
                }
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Edit;